<template>
  <div class="reader-image" :class="{ ls }">
    <div
      class="reader-image__image"
      :class="{
        'swiper-zoom-target': sourceReadingDirection !== 'vls',
        invisible: missingBlobUrls,
        ls,
        flex: image.pages.length > 1,
        'flex-row-reverse':
          sourceReadingDirection === 'rtl' || readingDirection === 'rtl',
      }"
    >
      <img
        v-for="page of image.pages"
        class="relative z-[5]"
        :class="{ upscale: upscaleSmallPages }"
        :src="page.blobUrl"
        alt="Image"
        :width="page.width"
        :height="page.height"
      />
      <template
        v-if="sourceReadingDirection === 'vls' && image.pages.length === 1"
      >
        <img
          class="absolute top-[-1px] bottom-[-1px] inset-x-0"
          :class="{ upscale: upscaleSmallPages }"
          alt="fill"
          :src="image.pages[0].blobUrl"
        />
      </template>
    </div>
    <Transition name="fade">
      <div
        v-if="missingBlobUrls"
        class="reader-image__placeholder"
        :class="{ ls, error: pageError }"
      >
        <AsyncImage
          class="mb-2 flex justify-center w-1/2"
          :src="
            pageError
              ? '/assets/nami/stickers/empty.png'
              : '/assets/nami/stickers/love.png'
          "
        />
        <div
          class="reader-image__placeholder-progress"
          :class="{ ls, error: pageError }"
        >
          <div class="relative text-center" style="z-index: 1">
            {{ pageError || imageProgress }}
          </div>
          <div
            v-if="!pageError"
            class="dark:none absolute top-0 left-0 w-full text-center text-white"
            style="z-index: 1"
            :style="{
              clipPath: `inset(0 ${100 - imageLoadProgress * 100}% 0 0 )`,
            }"
          >
            {{ imageProgress }}
          </div>
          <NamiButton
            v-else
            block
            button-type="secondary"
            :icon="IconReload"
            @click.stop="image.loadPages(true)"
            no-waves
          >
            {{ pageError ? "Retry" : "Load Image" }}
          </NamiButton>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import type { ImagePageGroup } from "~/src/reader/managers/pageGroup";
import type { TitleReadingMode } from "~/src/api";
import { IconReload } from "@tabler/icons-vue";
import type { UnwrapNestedRefs } from "vue";
import type { ReaderSettingsInterface } from "~/src/reader/static";

interface Props {
  sourceReadingDirection: TitleReadingMode;
  readerSettings: UnwrapNestedRefs<ReaderSettingsInterface>;
  image: ImagePageGroup;
  containerDimensions?: {
    width: number;
    height: number;
  };
}

const props = defineProps<Props>();

const missingBlobUrls = computed(() =>
  props.image.pages.some((page) => !page.blobUrl),
);

const { scrollDirection, readingDirection, imageFit, upscaleSmallPages } =
  toRefs(props.readerSettings);

const ls = computed(
  () =>
    scrollDirection.value === "vertical" ||
    props.sourceReadingDirection === "vls",
);

const imageLoadProgress = computed(() => props.image.getProgress());

const pageError = computed(
  () =>
    props.image.pages
      .map((i) => i.error && resolveError(i.error).title)
      .filter(onlyTruthys)[0],
);

const imageProgress = computed(
  () => (imageLoadProgress.value * 100).toFixed(0) + "%",
);
const aspect = computed(() => props.image.width / props.image.height);
const maxWidth = computed(() => {
  // determine our width max %, it is 1 if image is set to fit
  const customWidth =
    imageFit.value.current === "custom"
      ? imageFit.value.custom.maximumWidth
      : 1;
  // our max width, in pixels
  const maxWidth =
    (props.containerDimensions?.width ?? window?.innerWidth ?? 0) * customWidth;

  // if we upscale the pages, it will always be the max width we allow
  if (upscaleSmallPages.value) return `${maxWidth}px`;

  // otherwise, we return the natural width if it is smaller or equal to the max width
  return `${Math.min(maxWidth, props.image.width)}px`;
});
const maxHeight = computed(() => {
  // if it is a vertical long strip, the height is always determined on width
  if (ls.value) return "100%";

  // otherwise, compute it based of max dimensions
  const maxHeight =
    props.containerDimensions?.height ?? window?.innerHeight ?? 0;

  // if we upscale the pages, it will always be the max height we allow
  if (upscaleSmallPages.value) return `${maxHeight}px`;

  // otherwise, we return the natural height if it is smaller or equal to the max height
  return `${Math.min(maxHeight, props.image.height)}px`;
});
</script>

<style scoped lang="postcss">
.reader-image {
  direction: ltr;

  &:not(.ls) {
    @apply flex;
  }

  @apply relative select-none overflow-hidden;
}

.reader-image__image {
  aspect-ratio: v-bind(aspect);
  max-width: v-bind(maxWidth);
  height: auto;
  width: 100%;
  @apply relative;

  &.ls {
    height: v-bind(maxHeight);
    @apply mx-auto;
  }

  &:not(.ls) {
    width: v-bind(maxWidth);
    max-height: v-bind(maxHeight);
    max-width: calc(v-bind(maxHeight) * v-bind(aspect));
    @apply m-auto;
  }

  & .upscale {
    max-width: v-bind(maxWidth);
    @apply w-full;
  }
}

.reader-image__placeholder {
  width: v-bind(maxWidth);
  height: calc(v-bind(maxWidth) / v-bind(aspect));

  &:not(.ls) {
    max-height: v-bind(maxHeight);
    max-width: calc(v-bind(maxHeight) * v-bind(aspect));
  }

  @apply absolute top-0 left-0 right-0 bottom-0 m-auto
  p-4 bg-neutral-200 overflow-hidden border-2 border-neutral-300 rounded-md
  dark:bg-neutral-700 dark:border-neutral-600
  flex flex-col items-center justify-center;

  &:not(.error):before,
  &:not(.error):after {
    content: " ";
    opacity: 1;
    animation-duration: 1s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: shimmer;
    animation-timing-function: ease;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-width: 2px;
    border-style: solid;
    @apply absolute w-full h-full border-nami-nami-blue rounded-md;
  }

  &:before {
    animation-delay: 200ms;
  }
}

@keyframes shimmer {
  0% {
    opacity: 1;
    border-width: 8px;
    transform: translate(-50%, -50%) scale(0.8);
  }
  100% {
    opacity: 0;
    border-width: 0;
    transform: translate(-50%, -50%) scale(1);
  }
}

.reader-image__placeholder-progress {
  z-index: 3;
  max-width: 400px;
  @apply relative w-full mt-1 bg-neutral-100 rounded-md overflow-hidden
  dark:bg-neutral-500;

  &:not(.error):after {
    content: "";
    width: v-bind(imageProgress);
    @apply absolute left-0 top-0 h-full bg-nami-nami-blue;
  }

  &.error {
    @apply !bg-transparent text-red-500 font-bold text-2xl;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
